import React, { ReactNode, Component } from 'react'
import { css, cx } from '@emotion/css'
import { Label } from '../form-label'
import { toggleInputCss, toggleCss, disabledToggleCss } from './shared'
import { panelTitleCss } from '../text'
import { FormattedMessage } from 'react-intl'
import { LinkButton } from '../../design-system'

/**
 * Props that aren't otherwise explained are simply passed as attributes
 * to the input element
 */
interface Props {
  readonly 'aria-labelledBy'?: string
  readonly 'data-testid'?: string
  readonly disabled?: boolean

  /**
   * Toggles an EDITED indicator on the field
   */
  readonly edited?: boolean

  readonly id?: string

  /**
   * Will be passed as the ref to the actual input element
   */
  readonly inputRef?: (ref: HTMLInputElement) => void

  /**
   * Text describing the input element. This will be used as the content
   * for the label element unless the renderLabel prop is used
   */
  readonly label: string

  readonly onBlur?: () => void

  /**
   * onChange event handler for the input element. It will be
   * passed the value of the input as an argument.
   */
  readonly onChange?: (boolean) => void

  /**
   * Callback triggered when the edit button is triggered. Specifying
   * this callback will also render the the edit button when the control
   * is disabled
   */
  readonly onEdit?: () => void

  readonly onFocus?: () => void

  /**
   * If provided this callback will render the contents of a help tooltip. Also,
   * a help icon will appear as part of the control's label that activates the
   * tooltip when hovered.
   */
  readonly renderHelpContent?: () => ReactNode

  /**
   * Can be used to render an alternate label or exclude the label element
   * altogether if the callback returns null/undefined. If this callback is
   * defined it will be passed the value of the label prop as the first argument
   * and the input element id as the second argument.
   */
  readonly renderLabel?: (label: string, id?: string) => ReactNode

  readonly value?: boolean
}

export class ToggleInput extends Component<Props> {
  private inputRef: HTMLInputElement | undefined

  public render() {
    const {
      disabled,
      edited,
      id,
      inputRef,
      label,
      onBlur,
      onEdit,
      onFocus,
      onChange,
      renderHelpContent,
      renderLabel,
      value,
      ...props
    } = this.props

    const labelNode = renderLabel ? (
      renderLabel(label, id)
    ) : (
      <Label htmlFor={id} renderHelpContent={renderHelpContent}>
        {label}
      </Label>
    )

    const hasValue = value || value === false
    const changeHandler =
      onChange && hasValue ? () => onChange(!value) : undefined

    return (
      <div
        className={css`
          display: inline-block;
        `}
        onClick={() => {
          if (this.inputRef) {
            this.inputRef.focus()
          }
        }}
      >
        {labelNode}
        <div
          className={css`
            display: inline-flex;
            align-items: center;
            height: 34px;
          `}
          onClick={changeHandler}
        >
          <input
            // provide aria-label when we can't ensure a label is associated
            // with the control
            aria-label={renderLabel || !id ? label : undefined}
            aria-labelledby={props['aria-labelledBy']}
            checked={value}
            className={toggleInputCss}
            data-testid={props['data-testid']}
            disabled={disabled}
            id={id}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={changeHandler}
            ref={(ref) => {
              if (ref) {
                this.inputRef = ref
                if (inputRef) {
                  inputRef(ref)
                }
              }
            }}
            type="checkbox"
          />
          <i className={cx(toggleCss, { [disabledToggleCss]: disabled })} />
          {disabled && onEdit && (
            <LinkButton
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onEdit()
              }}
              className={css`
                margin-left: 10px;
                font-size: 12px;
              `}
            >
              Edit
            </LinkButton>
          )}
          {!disabled && edited && (
            <FormattedMessage id="detail_fieldEdited">
              {(text) => (
                <div
                  className={css`
                    ${panelTitleCss};
                    margin-left: 10px;
                  `}
                >
                  {text}
                </div>
              )}
            </FormattedMessage>
          )}
        </div>
      </div>
    )
  }
}
